/**
 * Content for the Porto Airport Private Transfer landing page.
 * Each language block is a self-contained SEO copy set.
 *
 * TO EDIT: replace the strings inside `pt` / `en` / `es` / `fr`.
 * The page renders whichever language is active. New fields you add here
 * will be automatically available inside the page component.
 */
export const AIRPORT_TRANSFER_CONTENT = {
  slug: 'porto-airport-private-transfer',

  // Universal (non-translated) metadata
  meta: {
    heroImage:
      'https://images.unsplash.com/photo-1568795924999-6ba0d4bacacc?crop=entropy&cs=srgb&fm=jpg&q=85',
    ogImage: 'https://govipportugal.com/logo-govip.jpg',
    published: '2026-01-20',
    updated: new Date().toISOString().slice(0, 10),
  },

  pt: {
    seo: {
      title: 'Transfer Privado Aeroporto Porto (OPO) | GO VIP Portugal — 24/7',
      description:
        'Transfer privado premium de e para o Aeroporto do Porto (OPO). Motorista profissional, rastreamento de voo, veículo de luxo e serviço 24/7. Reserve com preço fixo.',
      keywords:
        'transfer aeroporto porto, transfer opo, transfer privado porto, taxi aeroporto porto, motorista aeroporto porto',
    },
    hero: {
      kicker: 'Aeroporto do Porto (OPO)',
      title: 'Transfer Privado do Aeroporto do Porto',
      subtitle:
        'Motorista profissional à sua espera na chegada, veículo de luxo e preço fixo — 24 horas por dia, 7 dias por semana.',
      cta_primary: 'Reservar Agora',
      cta_secondary: 'WhatsApp Direto',
      badges: ['Rastreamento de Voo', 'Meet & Greet', 'Preço Fixo', 'Sem Cartão'],
    },
    benefits: {
      title: 'Porque escolher o transfer privado GO VIP?',
      items: [
        {
          title: 'Motorista à sua espera',
          text: 'Recebemo-lo à chegada com placa personalizada. Ajudamos com a bagagem até ao veículo.',
        },
        {
          title: 'Rastreamento de voo em tempo real',
          text: 'Monitorizamos o seu voo. Se atrasar, ajustamos o horário sem custo adicional.',
        },
        {
          title: 'Frota premium',
          text: 'Mercedes-Benz Classe E, V-Class e SUV luxo. Wi-Fi gratuito, água mineral e climatização.',
        },
        {
          title: 'Preço fixo — sem surpresas',
          text: 'Sabe exatamente quanto paga antes de reservar. Sem taxas de taxímetro nem custos extra por bagagem ou horário.',
        },
        {
          title: '24/7 · em qualquer horário',
          text: 'Voos noturnos, chegadas de madrugada ou fins-de-semana — estamos sempre disponíveis.',
        },
        {
          title: 'Motoristas bilingues',
          text: 'Falamos Português, Inglês, Espanhol e Francês. Conhecemos a cidade como ninguém.',
        },
      ],
    },
    howItWorks: {
      title: 'Como funciona',
      steps: [
        { n: '1', title: 'Reserve online', text: 'Preencha o formulário em 60 segundos.' },
        { n: '2', title: 'Confirmação por email', text: 'Recebe email com dados do motorista.' },
        { n: '3', title: 'Meet & Greet', text: 'Motorista à sua espera na sala de chegadas.' },
        { n: '4', title: 'Viaje em conforto', text: 'Chegue ao destino relaxado.' },
      ],
    },
    destinations: {
      title: 'Destinos mais populares desde o Aeroporto do Porto',
      subtitle: 'Preços fixos, todos os impostos incluídos. Preços indicativos por veículo (1–4 passageiros).',
      items: [
        { name: 'Centro do Porto (Baixa / Ribeira)', price: '€35', time: '20 min' },
        { name: 'Vila Nova de Gaia (Caves do Vinho)', price: '€40', time: '25 min' },
        { name: 'Braga', price: '€70', time: '40 min' },
        { name: 'Guimarães', price: '€75', time: '45 min' },
        { name: 'Aveiro', price: '€90', time: '55 min' },
        { name: 'Vale do Douro (Peso da Régua)', price: '€180', time: '1h30' },
        { name: 'Santiago de Compostela', price: '€250', time: '2h30' },
        { name: 'Lisboa', price: '€380', time: '3h00' },
      ],
    },
    faq: {
      title: 'Perguntas Frequentes',
      items: [
        {
          q: 'Quanto custa um transfer do Aeroporto do Porto para o centro?',
          a: 'Um transfer privado até ao centro do Porto (Baixa/Ribeira) tem preço fixo de €35, incluindo até 4 passageiros e bagagem. Não há custos extra por horário noturno ou bagagem adicional.',
        },
        {
          q: 'O que acontece se o meu voo atrasar?',
          a: 'Monitorizamos todos os voos em tempo real. Se o seu voo atrasar, o motorista ajusta o horário sem custo adicional. Está incluído no preço.',
        },
        {
          q: 'Como reconheço o motorista à chegada?',
          a: 'O motorista aguarda na sala de chegadas com uma placa personalizada com o seu nome. Também receberá o contacto direto por SMS/WhatsApp antes do voo.',
        },
        {
          q: 'Que tipos de veículos disponibilizam?',
          a: 'Mercedes-Benz Classe E para 1–3 passageiros, Mercedes V-Class para 4–7 passageiros com bagagem, e SUV premium sob pedido. Todos com Wi-Fi, água e climatização.',
        },
        {
          q: 'Posso pagar em dinheiro ao motorista?',
          a: 'Sim. Aceitamos dinheiro, MB Way, cartão (Visa/Mastercard) e transferência bancária. O pagamento pode ser feito online no ato da reserva ou diretamente ao motorista.',
        },
        {
          q: 'Fazem transfers para outros aeroportos (Lisboa, Vigo, Santiago)?',
          a: 'Sim. Fazemos transfers de e para o Aeroporto do Porto (OPO), Lisboa (LIS), Sá Carneiro, Vigo (VGO) e Santiago de Compostela (SCQ). Consulte-nos para preço.',
        },
      ],
    },
    cta: {
      title: 'Pronto para reservar o seu transfer?',
      subtitle: 'Chegue ao destino em conforto e sem preocupações.',
      button: 'Reservar Agora',
      whatsapp: 'Falar por WhatsApp',
    },
  },

  en: {
    seo: {
      title: 'Porto Airport Private Transfer (OPO) | GO VIP Portugal — 24/7',
      description:
        'Premium private transfer to and from Porto Airport (OPO). Professional driver, flight tracking, luxury vehicle and 24/7 service. Book with a fixed price — no surprises.',
      keywords:
        'porto airport transfer, opo transfer, private transfer porto, porto airport taxi, luxury airport transfer porto',
    },
    hero: {
      kicker: 'Porto Airport (OPO)',
      title: 'Porto Airport Private Transfer',
      subtitle:
        'A professional driver waiting for you on arrival, a luxury vehicle, and a fixed price — 24 hours a day, 7 days a week.',
      cta_primary: 'Book Now',
      cta_secondary: 'WhatsApp Us',
      badges: ['Flight Tracking', 'Meet & Greet', 'Fixed Price', 'No Card Needed'],
    },
    benefits: {
      title: 'Why choose GO VIP private transfers?',
      items: [
        {
          title: 'Driver waiting for you',
          text: "We meet you on arrival with a personalized sign. Help with luggage all the way to the car.",
        },
        {
          title: 'Real-time flight tracking',
          text: 'We monitor your flight. If delayed, we adjust — at no extra cost.',
        },
        {
          title: 'Premium fleet',
          text: 'Mercedes-Benz E-Class, V-Class and luxury SUV. Free Wi-Fi, mineral water and climate control.',
        },
        {
          title: 'Fixed price — no surprises',
          text: 'You know exactly what you pay before booking. No meter, no baggage or late-night fees.',
        },
        {
          title: '24/7 · any hour',
          text: 'Red-eye flights, early mornings, weekends — we are always available.',
        },
        {
          title: 'Bilingual drivers',
          text: 'English, Portuguese, Spanish, French. Local experts who know the city inside out.',
        },
      ],
    },
    howItWorks: {
      title: 'How it works',
      steps: [
        { n: '1', title: 'Book online', text: 'Fill in the form in 60 seconds.' },
        { n: '2', title: 'Email confirmation', text: 'Receive driver details by email.' },
        { n: '3', title: 'Meet & Greet', text: 'Driver waiting for you in Arrivals.' },
        { n: '4', title: 'Travel in comfort', text: 'Arrive relaxed at your destination.' },
      ],
    },
    destinations: {
      title: 'Most popular destinations from Porto Airport',
      subtitle: 'Fixed prices, all taxes included. Indicative prices per vehicle (1–4 passengers).',
      items: [
        { name: 'Porto City Centre (Baixa / Ribeira)', price: '€35', time: '20 min' },
        { name: 'Vila Nova de Gaia (Wine Cellars)', price: '€40', time: '25 min' },
        { name: 'Braga', price: '€70', time: '40 min' },
        { name: 'Guimarães', price: '€75', time: '45 min' },
        { name: 'Aveiro', price: '€90', time: '55 min' },
        { name: 'Douro Valley (Peso da Régua)', price: '€180', time: '1h30' },
        { name: 'Santiago de Compostela', price: '€250', time: '2h30' },
        { name: 'Lisbon', price: '€380', time: '3h00' },
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          q: 'How much is a transfer from Porto Airport to the city centre?',
          a: 'A private transfer to Porto city centre (Baixa/Ribeira) has a fixed price of €35, including up to 4 passengers and luggage. No extra fees for night hours or additional bags.',
        },
        {
          q: 'What if my flight is delayed?',
          a: 'We track all flights in real time. If your flight is delayed, the driver adjusts at no extra cost. It is already included.',
        },
        {
          q: 'How will I recognize the driver on arrival?',
          a: 'The driver waits in the Arrivals hall with a personalized sign showing your name. You will also receive direct contact via SMS/WhatsApp before your flight.',
        },
        {
          q: 'What kinds of vehicles do you offer?',
          a: 'Mercedes-Benz E-Class for 1–3 passengers, Mercedes V-Class for 4–7 passengers with luggage, and premium SUV on request. All with Wi-Fi, water and A/C.',
        },
        {
          q: 'Can I pay cash to the driver?',
          a: 'Yes. We accept cash, MB Way, cards (Visa/Mastercard) and bank transfer. You can pay online when booking or directly to the driver.',
        },
        {
          q: 'Do you serve other airports (Lisbon, Vigo, Santiago)?',
          a: 'Yes. We provide transfers to and from Porto (OPO), Lisbon (LIS), Vigo (VGO) and Santiago de Compostela (SCQ). Please contact us for a price.',
        },
      ],
    },
    cta: {
      title: 'Ready to book your transfer?',
      subtitle: 'Arrive at your destination in comfort and stress-free.',
      button: 'Book Now',
      whatsapp: 'Chat on WhatsApp',
    },
  },

  es: {
    seo: {
      title: 'Traslado Privado Aeropuerto Oporto (OPO) | GO VIP Portugal — 24/7',
      description:
        'Traslado privado premium desde/hacia el Aeropuerto de Oporto (OPO). Chófer profesional, seguimiento de vuelos, vehículo de lujo y servicio 24/7. Precio fijo, sin sorpresas.',
      keywords:
        'traslado aeropuerto oporto, transfer opo, traslado privado oporto, taxi aeropuerto oporto',
    },
    hero: {
      kicker: 'Aeropuerto de Oporto (OPO)',
      title: 'Traslado Privado desde el Aeropuerto de Oporto',
      subtitle:
        'Chófer profesional esperándole a la llegada, vehículo de lujo y precio fijo — 24 horas al día, 7 días a la semana.',
      cta_primary: 'Reservar Ahora',
      cta_secondary: 'WhatsApp Directo',
      badges: ['Seguimiento de Vuelo', 'Meet & Greet', 'Precio Fijo', 'Sin Tarjeta'],
    },
    benefits: {
      title: '¿Por qué elegir GO VIP?',
      items: [
        { title: 'Chófer esperándole', text: 'Le recibimos con un cartel personalizado. Ayudamos con el equipaje.' },
        { title: 'Seguimiento de vuelo en tiempo real', text: 'Si el vuelo se retrasa, ajustamos sin coste extra.' },
        { title: 'Flota premium', text: 'Mercedes-Benz Clase E, V-Class y SUV de lujo. Wi-Fi, agua y climatización.' },
        { title: 'Precio fijo — sin sorpresas', text: 'Sabe exactamente cuánto paga antes de reservar.' },
        { title: '24/7 · cualquier horario', text: 'Vuelos nocturnos, madrugadas o fines de semana.' },
        { title: 'Chóferes bilingües', text: 'Hablamos español, portugués, inglés y francés.' },
      ],
    },
    howItWorks: {
      title: 'Cómo funciona',
      steps: [
        { n: '1', title: 'Reserva online', text: 'Rellene el formulario en 60 segundos.' },
        { n: '2', title: 'Confirmación por email', text: 'Recibe los datos del chófer.' },
        { n: '3', title: 'Meet & Greet', text: 'Chófer esperándole en llegadas.' },
        { n: '4', title: 'Viaje en confort', text: 'Llegue relajado a su destino.' },
      ],
    },
    destinations: {
      title: 'Destinos más populares desde el Aeropuerto de Oporto',
      subtitle: 'Precios fijos, todos los impuestos incluidos. Precios indicativos por vehículo (1–4 pasajeros).',
      items: [
        { name: 'Centro de Oporto (Baixa / Ribeira)', price: '€35', time: '20 min' },
        { name: 'Vila Nova de Gaia (Bodegas)', price: '€40', time: '25 min' },
        { name: 'Braga', price: '€70', time: '40 min' },
        { name: 'Guimarães', price: '€75', time: '45 min' },
        { name: 'Aveiro', price: '€90', time: '55 min' },
        { name: 'Valle del Duero (Peso da Régua)', price: '€180', time: '1h30' },
        { name: 'Santiago de Compostela', price: '€250', time: '2h30' },
        { name: 'Lisboa', price: '€380', time: '3h00' },
      ],
    },
    faq: {
      title: 'Preguntas Frecuentes',
      items: [
        { q: '¿Cuánto cuesta un traslado desde el Aeropuerto de Oporto al centro?', a: 'Un traslado privado al centro de Oporto (Baixa/Ribeira) tiene un precio fijo de €35, incluyendo hasta 4 pasajeros y equipaje. Sin cargos extras por horario nocturno.' },
        { q: '¿Qué pasa si mi vuelo se retrasa?', a: 'Monitorizamos todos los vuelos en tiempo real. Si su vuelo se retrasa, el chófer ajusta el horario sin coste adicional.' },
        { q: '¿Cómo reconozco al chófer a la llegada?', a: 'El chófer le espera en la sala de llegadas con un cartel personalizado con su nombre. También recibirá el contacto directo por SMS/WhatsApp antes del vuelo.' },
        { q: '¿Qué tipos de vehículos ofrecen?', a: 'Mercedes-Benz Clase E para 1–3 pasajeros, Mercedes V-Class para 4–7 pasajeros con equipaje, y SUV premium bajo petición.' },
        { q: '¿Puedo pagar en efectivo al chófer?', a: 'Sí. Aceptamos efectivo, MB Way, tarjeta (Visa/Mastercard) y transferencia bancaria.' },
        { q: '¿Ofrecen traslados a otros aeropuertos?', a: 'Sí. Ofrecemos traslados desde/hacia Oporto (OPO), Lisboa (LIS), Vigo (VGO) y Santiago de Compostela (SCQ).' },
      ],
    },
    cta: {
      title: '¿Listo para reservar su traslado?',
      subtitle: 'Llegue a su destino con confort y sin preocupaciones.',
      button: 'Reservar Ahora',
      whatsapp: 'Hablar por WhatsApp',
    },
  },

  fr: {
    seo: {
      title: 'Transfert Privé Aéroport de Porto (OPO) | GO VIP Portugal — 24/7',
      description:
        "Transfert privé premium depuis/vers l'aéroport de Porto (OPO). Chauffeur professionnel, suivi de vol, véhicule de luxe et service 24/7. Prix fixe, sans surprises.",
      keywords:
        'transfert aéroport porto, transfert opo, transfert privé porto, taxi aéroport porto',
    },
    hero: {
      kicker: 'Aéroport de Porto (OPO)',
      title: "Transfert Privé depuis l'Aéroport de Porto",
      subtitle:
        "Un chauffeur professionnel vous attend à l'arrivée, un véhicule de luxe et un prix fixe — 24h/24, 7j/7.",
      cta_primary: 'Réserver',
      cta_secondary: 'WhatsApp',
      badges: ['Suivi de Vol', 'Meet & Greet', 'Prix Fixe', 'Sans Carte'],
    },
    benefits: {
      title: 'Pourquoi choisir GO VIP ?',
      items: [
        { title: "Chauffeur qui vous attend", text: "Nous vous accueillons à l'arrivée avec une pancarte personnalisée." },
        { title: 'Suivi de vol en temps réel', text: 'En cas de retard, nous ajustons sans frais supplémentaires.' },
        { title: 'Flotte premium', text: 'Mercedes-Benz Classe E, V-Class et SUV de luxe. Wi-Fi et climatisation.' },
        { title: 'Prix fixe — sans surprises', text: 'Vous savez exactement ce que vous payez avant de réserver.' },
        { title: '24/7 · à toute heure', text: 'Vols de nuit, tôt le matin ou weekends — toujours disponibles.' },
        { title: 'Chauffeurs bilingues', text: 'Français, portugais, anglais, espagnol.' },
      ],
    },
    howItWorks: {
      title: 'Comment ça marche',
      steps: [
        { n: '1', title: 'Réservez en ligne', text: 'Remplissez le formulaire en 60 secondes.' },
        { n: '2', title: 'Confirmation par email', text: 'Recevez les coordonnées du chauffeur.' },
        { n: '3', title: 'Meet & Greet', text: 'Chauffeur à votre attente aux arrivées.' },
        { n: '4', title: 'Voyagez confortablement', text: 'Arrivez détendu à destination.' },
      ],
    },
    destinations: {
      title: "Destinations les plus populaires depuis l'aéroport de Porto",
      subtitle: 'Prix fixes, toutes taxes incluses. Prix indicatifs par véhicule (1–4 passagers).',
      items: [
        { name: 'Centre de Porto (Baixa / Ribeira)', price: '€35', time: '20 min' },
        { name: 'Vila Nova de Gaia (Caves à vin)', price: '€40', time: '25 min' },
        { name: 'Braga', price: '€70', time: '40 min' },
        { name: 'Guimarães', price: '€75', time: '45 min' },
        { name: 'Aveiro', price: '€90', time: '55 min' },
        { name: 'Vallée du Douro', price: '€180', time: '1h30' },
        { name: 'Saint-Jacques-de-Compostelle', price: '€250', time: '2h30' },
        { name: 'Lisbonne', price: '€380', time: '3h00' },
      ],
    },
    faq: {
      title: 'Questions Fréquentes',
      items: [
        { q: "Combien coûte un transfert de l'aéroport de Porto au centre-ville ?", a: "Un transfert privé jusqu'au centre-ville de Porto (Baixa/Ribeira) coûte 35€ à prix fixe, jusqu'à 4 passagers et bagages inclus." },
        { q: 'Que se passe-t-il si mon vol est retardé ?', a: 'Nous suivons tous les vols en temps réel. Si votre vol est retardé, le chauffeur ajuste sans coût supplémentaire.' },
        { q: "Comment reconnaître le chauffeur à l'arrivée ?", a: 'Le chauffeur vous attend dans le hall des arrivées avec une pancarte personnalisée à votre nom.' },
        { q: 'Quels types de véhicules proposez-vous ?', a: 'Mercedes-Benz Classe E pour 1–3 passagers, Mercedes V-Class pour 4–7 passagers, et SUV premium sur demande.' },
        { q: 'Puis-je payer en espèces au chauffeur ?', a: 'Oui. Nous acceptons espèces, MB Way, carte bancaire (Visa/Mastercard) et virement.' },
        { q: 'Desservez-vous d\'autres aéroports ?', a: "Oui. Nous assurons les transferts depuis/vers Porto (OPO), Lisbonne (LIS), Vigo (VGO) et Saint-Jacques-de-Compostelle (SCQ)." },
      ],
    },
    cta: {
      title: 'Prêt à réserver votre transfert ?',
      subtitle: 'Arrivez à destination détendu et sans souci.',
      button: 'Réserver',
      whatsapp: 'Discuter sur WhatsApp',
    },
  },
};

export const getAirportTransferContent = (lang) => {
  return AIRPORT_TRANSFER_CONTENT[lang] || AIRPORT_TRANSFER_CONTENT.en;
};
